import styled, { css, keyframes } from 'styled-components'
import { COLORS } from 'consts/colors'
import { DURATIONS, EASING, RADII } from 'styles/consts'
import { TButtonType } from './types'

interface IButtonWrapperProps {
  $isLarge: boolean
  $type: TButtonType
  $hugePadding: boolean
  $isLoading?: boolean
  $isDisabled?: boolean
}

const buttonTypeStyles: Record<TButtonType, ReturnType<typeof css>> = {
  primary: css`
    background: ${COLORS.primary};
    color: ${COLORS.white};

    &:hover {
      background: ${COLORS.primaryHover};
    }
  `,
  secondary: css`
    padding: 12px 20px 11px 20px;
    background: ${COLORS.surface};
    color: ${COLORS.textPrimary};
    border: 1px solid ${COLORS.border};

    &:hover {
      background: ${COLORS.fill};
    }
  `,
  red: css`
    padding: 12px 20px 11px 20px;
    background: ${COLORS.danger};
    color: ${COLORS.white};

    &:hover {
      filter: brightness(0.92);
    }
  `,
  white: css`
    padding: 8px 20px;
    background: ${COLORS.surface};
    color: ${COLORS.textSecondary};

    &:hover {
      color: ${COLORS.textPrimary};
    }
  `,
  purple: css`
    padding: 16px 20px;
    background: ${COLORS.primary};
    color: ${COLORS.white};

    &:hover {
      background: ${COLORS.primaryHover};
    }
  `
}

export const ButtonWrapper = styled.button<IButtonWrapperProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: ${RADII.field};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background-color ${DURATIONS.fast} ${EASING.out},
    border-color ${DURATIONS.fast} ${EASING.out},
    color ${DURATIONS.fast} ${EASING.out},
    filter ${DURATIONS.fast} ${EASING.out},
    transform ${DURATIONS.fast} ${EASING.out};

  &:active {
    transform: scale(0.97);
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.primary};
    outline-offset: 2px;
  }

  ${({ $type }) => buttonTypeStyles[$type]};

  ${({ $type, $hugePadding }) => {
    if ($type === 'primary') {
      return css`
        padding: ${$hugePadding ? '11px 20px' : '8px 32px'};
      `
    }
    if ($type === 'secondary' || $type === 'red') {
      return css`
        padding: 12px 20px 11px 20px;
      `
    }
    if ($type === 'white') {
      return css`
        padding: 8px 20px;
      `
    }
    if ($type === 'purple') {
      return css`
        padding: 16px 20px;
      `
    }
    return ''
  }};

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
      cursor: default;
    `}

  ${({ $isLoading }) =>
    $isLoading &&
    css`
      background: ${COLORS.surface};
      border: 1px solid ${COLORS.primary};
      color: ${COLORS.primary};
    `}
`

const loadingAnimation = keyframes`
  0% { opacity: 0.3; }
  50% { opacity: 1; }
  100% { opacity: 0.3; }
`

export const LoadingDots = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '●●●';
    animation: ${loadingAnimation} 1.4s infinite ease-in-out;
    font-size: 12px;
    letter-spacing: 2px;
  }
`
